/**
 * O aviso de versão nova (13.14c, DW-367): "Há uma versão nova do CRM" e o botão "Recarregar".
 *
 * O chamado de 21/10 (P8, print 09) mostrou uma aba aberta desde antes do deploy de 13/10 com o
 * comportamento velho: ninguém sabia que havia versão nova. A resposta do Gustavo: o aviso, com um
 * "Recarregar" que faz **recarga forçada, limpando o cache**.
 *
 * **Como o SPA percebe — a assinatura dos arquivos.** O Vite põe o hash do conteúdo no nome de cada
 * arquivo do build (`/assets/index-<hash>.js`), e o `index.html` novo cita outros nomes. A assinatura
 * é a lista dos caminhos de **mesma origem sob `/assets/`** dos `script[type=module]` e dos
 * `link[rel=stylesheet]`, lida **uma vez**, no carregamento deste módulo — depois, o `import()` de um
 * pedaço lazy acrescenta `link` ao documento, e a comparação daria falso positivo. Script de extensão
 * do navegador (outra origem) e a barra de preview não contam. No desenvolvimento (que cita
 * `/src/main.tsx`) e no Vitest a assinatura sai vazia, e a conferência não liga.
 *
 * **Quando confere:** a aba que volta a ficar visível (o caso do chamado), a página restaurada do
 * bfcache (`pageshow` persistido, o Safari), a cada 5 minutos com a aba visível e o pedaço lazy que
 * não carregou (`vite:preloadError` — no deploy, o pedaço da versão velha sumiu e é `404`). Um
 * `fetch('/')` sem cache por vez, abortado em 10 s; só um HTML de verdade (`ok`, sem redirect,
 * `text/html`, com `/assets/`) com outra assinatura acende o aviso. O `preloadError` só confere:
 * rede instável também o dispara, e o erro de carga de hoje segue (sem `preventDefault`).
 *
 * **O estado mora no módulo** (`useSyncExternalStore`), e não no `Shell`: a virada de 768 px troca a
 * árvore e remonta o cabeçalho, e o aviso não pode sumir com ela.
 *
 * **A recarga forçada:** desregistra todo service worker e apaga todo Cache Storage (o CRM não tem
 * nenhum, mas o que houver sai), busca a URL atual com `cache: 'reload'` (o cache HTTP do navegador
 * recebe o `index.html` novo) e só então recarrega. Sem rede, não recarrega: a página de erro do
 * navegador seria pior que a versão velha.
 */

import { useSyncExternalStore } from 'react'

/** O intervalo da conferência com a aba visível. */
export const INTERVALO_DA_CONFERENCIA_EM_MS = 5 * 60 * 1000
/** O prazo de uma conferência: a pendurada é abortada e libera a próxima. */
export const PRAZO_DA_CONFERENCIA_EM_MS = 10_000

type Buscar = (url: string, opcoes?: RequestInit) => Promise<Response>

/** O que os testes trocam: a recarga do navegador (o jsdom não recarrega). */
export const ambienteDaVersao: { recarregar: () => void } = {
  recarregar: () => {
    window.location.reload()
  },
}

/** Os caminhos `/assets/` de mesma origem dos scripts de módulo e das folhas de estilo, ordenados. */
export function assinaturaDoDocumento(documento: Document, base: string): string[] {
  const origem = new URL(base).origin
  const atributos = [
    ...Array.from(documento.querySelectorAll('script[type="module"][src]'), (no) =>
      no.getAttribute('src'),
    ),
    ...Array.from(documento.querySelectorAll('link[rel="stylesheet"][href]'), (no) =>
      no.getAttribute('href'),
    ),
  ]
  const caminhos = new Set<string>()
  for (const atributo of atributos) {
    if (atributo === null) {
      continue
    }
    let endereco: URL
    try {
      endereco = new URL(atributo, base)
    } catch {
      continue
    }
    if (endereco.origin === origem && endereco.pathname.startsWith('/assets/')) {
      caminhos.add(endereco.pathname)
    }
  }
  return [...caminhos].sort()
}

/** A assinatura da página carregada — lida uma vez, antes de qualquer `import()`. */
const ASSINATURA_CARREGADA: readonly string[] =
  typeof document === 'undefined' ? [] : assinaturaDoDocumento(document, window.location.href)

let versaoNova = false
const ouvintes = new Set<() => void>()
let conferenciaNoAr = false

function avisar() {
  for (const ouvinte of ouvintes) {
    ouvinte()
  }
}

/** Se a conferência achou versão nova. */
export function haVersaoNova(): boolean {
  return versaoNova
}

/** Só para os testes: volta ao estado de quem acabou de carregar. */
export function reiniciarAVersaoNova(): void {
  versaoNova = false
  conferenciaNoAr = false
  ambienteDaVersao.recarregar = () => {
    window.location.reload()
  }
  avisar()
}

/** O aviso, para a tela: muda quando a conferência acha a versão nova. */
export function useVersaoNova(): boolean {
  return useSyncExternalStore(
    (ouvinte) => {
      ouvintes.add(ouvinte)
      return () => {
        ouvintes.delete(ouvinte)
      }
    },
    haVersaoNova,
    () => false,
  )
}

function iguais(uma: readonly string[], outra: readonly string[]): boolean {
  return uma.length === outra.length && uma.every((item, indice) => item === outra[indice])
}

/** Uma conferência: `true` quando o `index.html` servido tem outra assinatura. */
export async function conferirAVersao({
  buscar = (url, opcoes) => fetch(url, opcoes),
  assinatura = ASSINATURA_CARREGADA,
}: { buscar?: Buscar; assinatura?: readonly string[] } = {}): Promise<boolean> {
  if (assinatura.length === 0 || conferenciaNoAr) {
    return false
  }
  conferenciaNoAr = true
  const controle = new AbortController()
  const prazo = setTimeout(() => {
    controle.abort()
  }, PRAZO_DA_CONFERENCIA_EM_MS)
  try {
    const resposta = await buscar('/', {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
      signal: controle.signal,
    })
    const tipo = resposta.headers.get('content-type') ?? ''
    if (!resposta.ok || resposta.redirected || !tipo.includes('text/html')) {
      return false
    }
    const html = await resposta.text()
    const servida = assinaturaDoDocumento(
      new DOMParser().parseFromString(html, 'text/html'),
      window.location.href,
    )
    if (servida.length === 0 || iguais(servida, assinatura)) {
      return false
    }
    if (!versaoNova) {
      versaoNova = true
      avisar()
    }
    return true
  } catch {
    return false
  } finally {
    clearTimeout(prazo)
    conferenciaNoAr = false
  }
}

/** Liga os gatilhos da conferência; devolve quem os desliga. */
export function iniciarAConferencia(
  opcoes: { buscar?: Buscar; assinatura?: readonly string[] } = {},
): () => void {
  const conferir = () => {
    void conferirAVersao(opcoes)
  }
  const aoMudarAVisibilidade = () => {
    if (document.visibilityState === 'visible') {
      conferir()
    }
  }
  const aoMostrarAPagina = (evento: Event) => {
    if ((evento as PageTransitionEvent).persisted) {
      conferir()
    }
  }
  document.addEventListener('visibilitychange', aoMudarAVisibilidade)
  window.addEventListener('pageshow', aoMostrarAPagina)
  window.addEventListener('vite:preloadError', conferir)
  const intervalo = setInterval(() => {
    if (document.visibilityState === 'visible') {
      conferir()
    }
  }, INTERVALO_DA_CONFERENCIA_EM_MS)
  return () => {
    document.removeEventListener('visibilitychange', aoMudarAVisibilidade)
    window.removeEventListener('pageshow', aoMostrarAPagina)
    window.removeEventListener('vite:preloadError', conferir)
    clearInterval(intervalo)
  }
}

/**
 * A recarga forçada do "Recarregar": `true` quando recarregou; `false` sem rede, com resposta que não
 * é `ok` (o `5xx` do meio do deploy) ou pendurada além do prazo — a tela diz por quê e devolve o
 * botão.
 */
export async function recarregarSemCache({
  buscar = (url, opcoes) => fetch(url, opcoes),
}: { buscar?: Buscar } = {}): Promise<boolean> {
  try {
    const registros = await navigator.serviceWorker?.getRegistrations()
    await Promise.all((registros ?? []).map((registro) => registro.unregister()))
  } catch {
    // Sem service worker (ou sem permissão): nada a desregistrar.
  }
  try {
    if (typeof caches !== 'undefined') {
      const nomes = await caches.keys()
      await Promise.all(nomes.map((nome) => caches.delete(nome)))
    }
  } catch {
    // Sem Cache Storage (o contexto que não é seguro): nada a apagar.
  }
  const controle = new AbortController()
  const prazo = setTimeout(() => {
    controle.abort()
  }, PRAZO_DA_CONFERENCIA_EM_MS)
  try {
    const resposta = await buscar(`${window.location.pathname}${window.location.search}`, {
      cache: 'reload',
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
      signal: controle.signal,
    })
    //: O `502` do meio do deploy, a página de manutenção: recarregar cairia nela.
    if (!resposta.ok) {
      return false
    }
  } catch {
    return false
  } finally {
    clearTimeout(prazo)
  }
  ambienteDaVersao.recarregar()
  return true
}
